import * as React from 'react';
import classNames from 'classnames';
import { Popover, Position as BlueprintPosition } from '@blueprintjs/core';

import Tooltip from '../Tooltip';
import Icon, { IconsAvailable } from '../Icon';

import './styles.scss';
import { compose } from 'redux';
import withStyles, { WithStyles } from 'react-jss';

export interface IButtonIconProps {
    /** style to pass */
    style?: React.CSSProperties;
    /** size to use */
    size?: 'large' | 'default' | 'small' | 'medium';
    /** click event */
    onClick?: (event) => void;
    /** intent variation */
    intent?: 'default' | 'primary' | 'success' | 'danger' | 'link' | 'neutral' | 'secondary';
    /** disable clicking */
    disabled?: boolean;
    /** disable clicking and display loader */
    loading?: boolean;
    /** type of element to use */
    type?: 'button' | 'submit' | 'a';
    /** Disable shadow */
    disableShadow?: boolean;
    /** Icon to use within button */
    icon: IconsAvailable;
    /** className to append */
    className?: string;
    /** DOM element name attribute */
    name?: string;
    /** DOM element id attribute */
    id?: string;
    /** Label to display */
    label?: string;
    /** Accessible label */
    accessibleLabel?: string;
    /** Dropdown menu content */
    menu?: React.ReactElement;
    /** style to use */
    layout?: 'default' | 'icon-only';
    /** Tooltip text to display on hover */
    tooltip?: string;
    ariaConfig?: IAriaConfig;
}

export interface IAriaConfig {
    label?: string;
    pressed?: boolean;
}

export type AnimationOptions = 'bounce';

interface IState {
    isOpen: boolean;
}

type Props = IButtonIconProps & WithStyles<typeof styles>;

/**
 * Displays button
 */
class ButtonIcon extends React.Component<Props, IState> {
    static displayName: string = 'ButtonIcon';

    constructor (props: Props) {
        super(props);
        this.state = {
            isOpen: false,
        };
    }

    handleClosePopover = (e?: React.SyntheticEvent<HTMLElement>) => {
        this.setState({
            isOpen: false,
        });
    };

    handleToggle = (e) => {
        e.preventDefault();
        this.setState({
            isOpen: true,
        });
    };

    render () {
        let Elem: any = 'button';
        if (this.props.type && this.props.type === 'a') {
            Elem = 'a';
        }

        const RenderButton = () => {
            return (
                <div className="button-icon-inner">
                    <div className="button-icon-main">
                        <Icon
                            width={16}
                            height={16}
                            kind={this.props.icon}
                            wrapperClassName="icon"
                        />
                        {this.props.loading && (
                            <div className="loader">
                                <Icon width={12} height={12} kind="loading"/>
                            </div>
                        )}
                    </div>
                    {!!this.props.label && (
                        <div className="label">{this.props.label}</div>
                    )}
                </div>
            );
        };

        return (
            <Elem
                type={this.props.type && this.props.type === 'submit' ? 'submit' : undefined}
                className={classNames(this.props.classes.button, 'button-icon', this.props.className, {
                    'disabled': this.props.disabled,
                    'loading': this.props.loading,
                    'noshadow': this.props.disableShadow,

                    'size-medium': this.props.size === 'medium',
                    'size-small': this.props.size === 'small',
                    'size-large': this.props.size === 'large',

                    'intent-default': this.props.intent === 'default' || !this.props.intent,
                    'intent-primary': this.props.intent === 'primary',
                    'intent-secondary': this.props.intent === 'secondary',
                    'intent-success': this.props.intent === 'success',
                    'intent-danger': this.props.intent === 'danger',
                    'intent-link': this.props.intent === 'link',
                    'intent-neutral': this.props.intent === 'neutral',

                    'layout-default': this.props.layout === 'default' || !this.props.layout,
                    'layout-icon-only': this.props.layout === 'icon-only',
                })}
                style={this.props.style}
                onClick={(e) => {
                    if (this.props.menu) {
                        this.handleToggle(e);
                    }
                    if (this.props.onClick) {
                        this.props.onClick(e);
                    }
                }}
                aria-label={this.props.ariaConfig?.label ?? this.props.accessibleLabel}
                aria-pressed={this.props.ariaConfig?.pressed}
                name={this.props.name}
                id={this.props.id}
            >
                {this.props.menu ? (
                    <Popover
                        content={this.props.menu}
                        target={(
                            <RenderButton/>
                        )}
                        hasBackdrop={false}
                        isOpen={this.state.isOpen}
                        onClose={this.handleClosePopover}
                        portalClassName={'popover-default-portal'}
                        popoverClassName={'popover-default-popover nopadding'}
                        position={BlueprintPosition.BOTTOM}
                        minimal={true}
                        usePortal={true}
                        modifiers={{
                            offset: {
                                offset: '0, 10',
                            },
                        }}
                    />
                ) : (
                    <RenderButton/>
                )}
                {this.props.tooltip && (
                    <Tooltip
                        position="top"
                        interactionKind="hover"
                        usePortal={true}
                        content={this.props.tooltip}
                        children={<div/>}
                        className="button-tooltip"
                    />
                )}
            </Elem>
        );
    }
}

const styles = (theme) => {
    return {
        button: {
            'color': theme.colors.text.dark,
            '&.layout-default': {
                '&.intent-default .button-icon-main': {
                    'color': theme.colors.gray.light,
                    'backgroundColor': theme.colors.gray.light,
                    '& .loader, & .icon': {
                        color: theme.colors.text.dark,
                    },
                },
                '&.intent-primary .button-icon-main': {
                    color: theme.colors.primary,
                    backgroundColor: theme.colors.primary,
                },
                '&.intent-secondary .button-icon-main': {
                    color: theme.colors.text.light,
                    backgroundColor: 'transparent',
                    borderColor: theme.colors.primary,
                },
                '&.intent-link .button-icon-main': {
                    'color': theme.colors.gray.light,
                    '& .loader, & .icon': {
                        color: theme.colors.text.dark,
                    },
                },
            },
            '& .loader, & .icon': {
                color: theme.colors.white,
            },
        },
    };
};

export default compose<React.ComponentClass<IButtonIconProps>>(
    withStyles(styles, { injectTheme: true })
)(ButtonIcon);
